import React from 'react';

import { Icon, addWithConfig } from '@rbilabs/universal-components';
import { StyleSheet } from 'react-native';
import type { ViewStyle } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import LoyaltyPointsIcon from 'components/icons/loyalty-points-icon';
import { IncentiveEvaluationMessages } from 'components/incentive-evaluation-messages/incentive-evaluation-messages';
import { DisclaimerContainer } from 'components/offer-disclaimer/offer-disclaimer';
import Picture from 'components/picture';
import { ProgressMeter } from 'components/progress-meter';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { Box, withTypography } from 'components/ui';
import type { BoxProps } from 'components/ui';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { ZIndex, getDesignToken, primitive, spacing } from 'styles/constants';

import { RewardPointCost } from '../reward-point-cost';

type ButtonsWrapperProps = BoxProps & {
  $errorMessage?: boolean;
  justifyContent?: ViewStyle['justifyContent'];
  margin?: '$0';
  padding?: '$0';
};

type PointsContainerProps = BoxProps & {
  marginTop?: '$0';
};

type PointsTextContainerProps = BoxProps & {
  marginBottom?: '$4';
};

const styles = StyleSheet.create({
  imageWrapper: {
    alignItems: 'center',
    paddingTop: spacing.$2,
  },
  rewardName: {
    marginBottom: spacing.$1,
    marginTop: spacing.$4,
    maxWidth: 960,
    textAlign: 'center',
  },
  rewardExpiration: {
    marginTop: spacing.$3,
  },
  buttonContainer: {
    flexShrink: 0,
    maxWidth: '100%',
    width: '100%',
  },
  rewardDetailsContainer: {
    alignItems: 'center',
    gap: spacing.$2,
    marginBottom: spacing.$4,
    paddingHorizontal: spacing.$0,
  },
  pointsContainer: {
    alignItems: 'center',
    marginTop: spacing.$6,
  },
  pointsContainerNoMarginTop: {
    marginTop: spacing.$0,
  },
  pointsTextContainer: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  pointsTextContainerWithMarginBottom: {
    marginBottom: spacing.$4,
  },
  buttonsWrapper: {
    alignItems: 'center',
    marginVertical: spacing.$4,
    paddingBottom: spacing.$4,
    paddingHorizontal: spacing.$4,
    paddingTop: spacing.$4,
    zIndex: ZIndex.TOP,
  },
  buttonsWrapperWithError: {
    marginTop: spacing.$0,
  },
  buttonsWrapperWithoutError: {
    marginTop: spacing.$4,
  },
  buttonsWrapperDesktop: {
    paddingTop: spacing.$8,
  },
  buttonsWrapperCentered: {
    justifyContent: 'center',
  },
  buttonsWrapperNoMargin: {
    margin: spacing.$0,
  },
  buttonsWrapperNoPadding: {
    padding: spacing.$0,
  },
  cardFill: {
    backgroundColor: primitive.$mayoEggWhite,
    flex: 1,
  },
});

export const incentiveDetailsCardFillStyle = styles.cardFill;

export const ImageWrapper = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.imageWrapper, style] });

export const StyledRewardImage = Picture.withConfig({
  width: '100%',
  zIndex: ZIndex.NORMAL,
});

export const BackgroundImage = Picture.withConfig({
  position: 'absolute',
  left: 0,
  top: 0,
  width: 'full',
  height: '100%',
});

export const RewardName = withTypography({
  variant: 'headerOne',
  textAlign: 'center',
  fontSize: '2xl',
  lineHeight: 'md',
  style: styles.rewardName,
});

export const RewardDescription = SanityBlockRenderer.withConfig({
  color: primitive.$greyOne,
  _text: {
    textAlign: 'center',
    fontSize: 'sm',
  },
  textProps: { fontSize: 'md', textAlign: 'center' },
});

export const RewardExpiration = withTypography({
  color: primitive.$greyOne,
  style: styles.rewardExpiration,
});

export const ButtonContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.buttonContainer, style] });

export const RewardDetailsContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.rewardDetailsContainer, style] });

export const DisclaimerText = SanityBlockRenderer.withConfig({
  maxWidth: '512px',
  marginY: '$8',
  marginX: 'auto',
  textProps: {
    color: primitive.$greyOne,
    textAlign: 'center',
    fontSize: 'xs',
  },
});

export const StyledProgressMeter = ({
  style,
  ...props
}: React.ComponentProps<typeof ProgressMeter>) =>
  React.createElement(ProgressMeter, {
    ...props,
    style: [
      { backgroundColor: primitive.$white, borderWidth: 0, marginVertical: spacing.$4, width: 240 },
      style,
    ],
  });

export const PointsContainer = ({ marginTop, style, ...props }: PointsContainerProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.pointsContainer, marginTop === '$0' && styles.pointsContainerNoMarginTop, style],
  });

export const PointsTextContainer = ({ marginBottom, style, ...props }: PointsTextContainerProps) =>
  React.createElement(Box, {
    ...props,
    style: [
      styles.pointsTextContainer,
      marginBottom === '$4' && styles.pointsTextContainerWithMarginBottom,
      style,
    ],
  });

export const StyledRewardPointCost = RewardPointCost.withConfig({
  fontSize: '2xl',
  bold: true,
  style: { alignItems: 'center' as any },
});

export const StyledPointsIcon = LoyaltyPointsIcon.withConfig({
  width: '32px',
  height: '32px',
  marginRight: '8px',
  fill: '#FFAA00',
});

export const StyledRewardLockIcon = Icon.withConfig({
  variant: 'locked',
  size: 7,
  color: getDesignToken('icon-default'),
});

export const StyledIncentiveEvaluationMessages = IncentiveEvaluationMessages.withConfig({
  textAlign: 'center',
});

export const AddToCartButton = addWithConfig(ActionButton).withConfig<{ isOffer?: boolean }>(
  props => ({
    fontSize: 'lg',
    justifyContent: 'center',
    paddingX: '$12',
    minHeight: '48px',
    width: 'full',
    textDecorationColor: primitive.$bbqBrown,

    ...(!props.isOffer && {
      backgroundColor: primitive.$meltyYellow,
      _text: { color: primitive.$bbqBrown },
      _hover: { backgroundColor: primitive.$meltyYellowDark45 },
      _pressed: { backgroundColor: primitive.$meltyYellowDark40 },
      color: primitive.$bbqBrown,
    }),
  })
);

export const RedeemInRestaurantButton = addWithConfig(ActionButton).withConfig<{
  isOffer?: boolean;
}>(props => ({
  fontSize: 'lg',
  justifyContent: 'center',
  paddingX: '$12',
  minHeight: '48px',
  width: 'full',
  textDecorationColor: primitive.$bbqBrown,

  ...(!props.isOffer && {
    borderColor: primitive.$bbqBrown,
    _text: { color: primitive.$bbqBrown },
    _hover: {
      backgroundColor: primitive.$bbqBrownOpacity15,
      borderColor: primitive.$bbqBrown,
    },
    _pressed: {
      backgroundColor: primitive.$bbqBrownOpacity15,
      borderColor: primitive.$bbqBrown,
    },
  }),
}));

export const RemoveFromCartButton = addWithConfig(ActionButton).withConfig({
  fontSize: 'lg',
  justifyContent: 'center',
  paddingX: '$12',
  height: '48px',
  variant: ActionButtonVariants.OUTLINE,
});

export const ButtonsWrapper = ({
  $errorMessage,
  justifyContent,
  margin,
  padding,
  style,
  ...props
}: ButtonsWrapperProps) => {
  const isDesktop = useIsDesktopBreakpoint();

  return React.createElement(Box, {
    ...props,
    style: [
      styles.buttonsWrapper,
      $errorMessage ? styles.buttonsWrapperWithError : styles.buttonsWrapperWithoutError,
      isDesktop && styles.buttonsWrapperDesktop,
      justifyContent === 'center' && styles.buttonsWrapperCentered,
      margin === '$0' && styles.buttonsWrapperNoMargin,
      padding === '$0' && styles.buttonsWrapperNoPadding,
      style,
    ],
  });
};

export const StyledDisclaimerContainer = DisclaimerContainer.withConfig({
  marginTop: '$4',
});
